// eslint-disable-next-line no-unused-vars
import React, { memo, useEffect, useCallback, useMemo, useState } from 'react';
import { node } from 'prop-types';
import { createContext, useContextSelector } from 'use-context-selector';
import useSWR, { useSWRConfig } from 'swr';
import noop from 'lodash/noop';
import debounce from 'lodash/debounce';

import { API_WISHLISTS } from 'Constants';
import useFetch from 'Hooks/useFetch';
import { UserContext } from 'Contexts/UserContext';
import useBreadcrumbs from 'Hooks/useBreadcrumbs';

export const stockAlertWishlistId = 'stock-alert-list';

export const WishlistContext = createContext({
  data: null,
  error: undefined,
  isLoading: false,
  defaultWishlist: null,
  stockAlertWishlist: null,
  removeProduct: noop,
  updateProduct: noop,
  createList: noop,
  getList: noop,
  updateList: noop,
  deleteList: noop
});

const WishlistProvider = memo(({ children }) => {
  const [err, setErr] = useState(null);
  const { cache } = useSWRConfig();
  const user = useContextSelector(UserContext, ({ data }) => ({
    loggedIn: data?.loggedIn,
    email: data?.email,
    defaultCurrency: data?.defaultCurrency,
    withVat: data?.withVat
  }));
  const fetcher = useFetch();
  const cacheWishlists = cache.get('wishlists');
  const [wishlists, setWishlists] = useState(
    (cacheWishlists || [])
      .reduce((acc, wishlist) => {
        acc[wishlist.id] = wishlist;
        return acc;
      }, {})
  );

  // TODO
  const [isLoading, setLoading] = useState(
    false
    // user?.email
    // && user?.loggedIn
    // && !cacheWishlists
  );
  const breadcrumbs = useBreadcrumbs();

  const response = useSWR(
    (user?.email && user?.loggedIn) ? `${ API_WISHLISTS }?uid=${ user.email }` : null,
    async path => {
      try {
        setLoading(true);
        const data = await fetcher(path);
        if (data) {
          setLoading(false);
        }
        return data;
      } catch (e) {
        setErr(e.message);
        setLoading(false);
        return e;
      }
    },
    {
      refreshInterval: 1000 * 60 * 15,
      revalidateOnMount: !cacheWishlists
    }
  );

  useEffect(() => {
    if (
      user?.loggedIn
      && response?.data
    ) {
      response.mutate();
    }
  }, [
    user?.loggedIn,
    user?.defaultCurrency,
    user?.withVat
  ]);

  const getWishlist = async (id, userDefaultCurrency, userDefaultWithVat) => {
    try {
      setErr(null);
      const currency = userDefaultCurrency ?? user?.defaultCurrency;
      const vat = userDefaultWithVat ?? user?.withVat;
      const query = [
        currency !== undefined && currency !== null
          ? `currency=${ encodeURIComponent(currency) }`
          : null,
        vat !== undefined && vat !== null
          ? `vat=${ encodeURIComponent(vat) }`
          : null
      ].filter(Boolean).join('&');
      const r = await fetcher(`${ API_WISHLISTS }/${ id }${ query ? `?${ query }` : '' }`);
      if (r) {
        setWishlists(prevState => ({
          ...prevState,
          [id]: {
            ...(prevState[id] || {}),
            ...r
          }
        }));
      }
      return r;
    } catch (e) {
      if (e.status === 404) {
        setErr('Aceasta lista nu mai exista sau nu a fost gasita.');
      } else {
        setErr(e.message);
      }

      throw e;
    }
  };

  /**
   * Updates wishlist
   * @param id
   * @param wishlist
   * @returns {Promise<*>}
   */
  const handleWishlist = async (id, wishlist = {}) => {
    const payload = { ...wishlist };
    const cacheKey = { ...payload };
    const products = (wishlist?.products || []).slice();

    if (products.length) {
      cacheKey.products = products.map(p => p.id || p.productId).join(',');
      payload.products = products;
    }

    try {
      const r = await fetcher(`${ API_WISHLISTS }/${ id }?currency=${ user?.defaultCurrency }&vat=${ user?.withVat }`, {
        method: 'PUT',
        forceCancel: true,
        cacheKey: `${ API_WISHLISTS }/${ id }/?cacheKey=${ JSON.stringify(cacheKey) }&currency=${ user?.defaultCurrency }&vat=${ user?.withVat }`,
        body: JSON.stringify(payload)
      });
      if (r) {
        setWishlists(prevState => ({
          ...prevState,
          [id]: {
            ...prevState[id],
            ...r
          }
        }));
        return r;
      }
    } catch (e) {
      setErr(e.message);
      throw e;
    }
    return null;
  };

  const updateWishlist = useCallback(
    (id, data) => {
      setErr(null);
      const existingWishlistData = wishlists[id] || {};
      const existingProducts = existingWishlistData?.products?.slice() || [];
      const newItemsState = (data?.products || []).reduce((acc, prod) => {
        const idx = acc.findIndex(i => (i.id || i.productId) === (prod.id || prod.productId));
        if (idx !== -1) {
          const foundProduct = acc[idx];
          acc.splice(idx, 1, {
            ...foundProduct,
            ...prod
          });
        }
        return acc;
      }, existingProducts);
      const newSrvItems = (data?.products || [])
        .reduce((acc, prod) => {
          const existingProduct = existingProducts.find(i => (i.id || i.productId) !== (prod.id || prod.productId));
          acc.push({
            productId: prod.id || prod.productId,
            quantity: existingProduct
              ? existingProduct.quantity
              : prod.quantity
          });
          return acc;
        }, []);
      // optimistic update
      const newWishlist = {
        ...existingWishlistData,
        ...data,
        products: newItemsState
      };
      setWishlists(prevState => ({
        ...prevState,
        [id]: newWishlist
      }));
      // setToHandle(prevState => omit(prevState, [id]));
      const payload = {
        ...data
      };
      if ('products' in data) {
        payload.products = newSrvItems;
      }
      return handleWishlist(id, payload);
    },
    [wishlists]
  );

  const debouncedUpdateWishlist = useCallback(
    debounce(updateWishlist, 500, { leading: true, trailing: true }),
    [updateWishlist]
  );

  /**
   * Create a wishlist
   * @param {{ id: string, default: boolean, products: array<id: string, quantity: number> }} data
   * @returns {Promise<*>}
   */
  const createWishlist = async data => {
    try {
      setLoading(true);
      setErr(null);
      const r = await fetcher(`${ API_WISHLISTS }?currency=${ user?.defaultCurrency }&vat=${ user.withVat }`, {
        method: 'POST',
        body: JSON.stringify(data)
      });
      if (r) {
        setLoading(false);
        response.mutate(response.data.concat(r), false);
      }
      return r;
    } catch (e) {
      setErr(e.message);
      setLoading(false);
      throw e;
    }
  };

  /**
   * Delete wishlist
   * @param {string|number} id
   * @returns {Promise<void>}
   */
  const deleteWishlist = async id => {
    try {
      setLoading(true);
      setErr(null);
      const r = await fetcher(`${ API_WISHLISTS }/${ id }?currency=${ user?.defaultCurrency }&vat=${ user.withVat }`, {
        method: 'DELETE'
      });
      setLoading(false);
      response.mutate(response.data.filter(i => i.id !== id), false);
      setWishlists(prevState => {
        // eslint-disable-next-line no-param-reassign
        delete prevState[id];
        return { ...prevState };
      });
      return r;
    } catch (e) {
      setErr(e.message);
      setLoading(false);
      throw e;
    }
  };

  /**
   * Move product from one list to another
   * @param fromList
   * @param toList
   * @param product
   * @returns {Promise<void>}
   */
  const handleMoveProduct = async (fromList, toList, product) => {
    try {
      setErr(null);
      await fetcher(`${ API_WISHLISTS }/${ fromList }/${ product.productId }/move?currency=${ user?.defaultCurrency }&vat=${ user.withVat }`, {
        method: 'PUT',
        body: JSON.stringify({
          target: toList,
          product
        })
      });
      const existingFrom = wishlists[fromList] || {};
      const existingTo = wishlists[toList] || {};
      setWishlists(prevState => ({
        ...prevState,
        [fromList]: {
          ...existingFrom,
          products: (existingFrom?.products || []).reduce((acc, item) => {
            if (product.productId !== (item.id || item.productId)) {
              acc.push(item);
            }
            return acc;
          }, [])
        },
        [toList]: {
          ...existingTo,
          products: (existingTo?.products || []).reduce((acc, item) => {
            if (product.productId === (item.id || item.productId)) {
              // eslint-disable-next-line no-param-reassign
              item.quantity += product.quantity;
            }
            acc.push(item);
            return acc;
          }, [])
        }
      }));
    } catch (e) {
      setErr(e.message);
      throw e;
    }
  };

  /**
   * Remove product from wishlist
   * @param {string} wishlistId
   * @param {string} productId
   * @returns {Promise<void>}
   */
  const removeProduct = (wishlistId, productId) => handleWishlist(wishlistId, {
    products: [{
      productId,
      quantity: 0
    }]
  });

  /**
   * Update wishlist product
   * @param {string} wishlistId
   * @param {object} product
   * @returns {Promise<void>}
   */
  const updateProduct = (wishlistId, product) => handleWishlist(wishlistId, {
    products: [{
      productId: product.id || product.productId,
      quantity: product.quantity
    }]
  });

  /**
   * Update multiple wishlist products
   * @param {string} wishlistId
   * @param {array} products
   * @returns {Promise<void>}
   */
  const updateProducts = (wishlistId, products) => handleWishlist(wishlistId, {
    products: products.map(product => ({
      productId: product.id || product.productId,
      quantity: product.quantity
    }))
  });

  useEffect(() => {
    if (
      Array.isArray(response?.data)
      && response?.data.length
      && !response.data.every(list => list.id in wishlists)
    ) {
      setWishlists(prevWishlists => response.data
        .reduce((acc, wishlist) => {
          let existingData = {};
          if (wishlist.id in (prevWishlists || {})) {
            existingData = prevWishlists[wishlist.id];
          }
          acc[wishlist.id] = {
            ...existingData,
            ...wishlist
          };
          return acc;
        }, {})
      );
    }
  }, [response.data]);

  useEffect(() => {
    if (wishlists) {
      breadcrumbs.setBreadcrumbs(prevState => ({
        ...prevState,
        lists: wishlists || {}
      }));
    }
  }, [wishlists]);

  const memoizedWishlists = useMemo(() => {
    const {
      all,
      stockAlertWishlist,
      defaultWishlist
    } = Object.values(wishlists).reduce((acc, list) => {
      if (!list?.id) return acc;
      if (list.id === stockAlertWishlistId) {
        acc.stockAlertWishlist = list;
        return acc;
      }
      if (list.isDefault) {
        acc.defaultWishlist = list;
      }
      acc.all[list.id] = list;
      return acc;
    }, { stockAlertWishlist: null, defaultWishlist: null, all: {} });
    return {
      data: all,
      error: err,
      isLoading,
      defaultWishlist,
      stockAlertWishlist,
      updateProduct,
      updateProducts,
      moveProduct: handleMoveProduct,
      removeProduct,
      getList: getWishlist,
      createList: createWishlist,
      updateList: debouncedUpdateWishlist,
      deleteList: deleteWishlist
    };
  }, [isLoading, wishlists, err]);

  return (
    <WishlistContext.Provider value={ memoizedWishlists }>
      { children }
    </WishlistContext.Provider>
  );
});

WishlistProvider.propTypes = {
  children: node
};

WishlistProvider.displayName = 'WishlistProvider';

export default WishlistProvider;
